import { FaDollarSign } from "react-icons/fa";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Card } from "../common/Card";
import { ErrorRetry } from "../../common/ErrorRetry";
import { ListShimmer } from "../../common/reportDashBShimmer/ListShimmer";
import { FLIGHT_CHART_COLORS } from "./flightReportUtils";

export function FlightRevenueSection({
  classState,
  streamsState,
  revenueChartData,
  revenueStreams,
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="p-8 bg-white rounded-2xl shadow-lg shadow-gray-100/50 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
        <div className="mb-6">
          <span className="text-[#1A1A1A] text-base md:text-lg mb-1">
            Revenue by Class
          </span>
          <p className="text-sm text-[#6B7280]">
            Revenue distribution across cabin classes
          </p>
        </div>

        {classState.loading ? (
          <ListShimmer rows={4} />
        ) : classState.error ? (
          <ErrorRetry message={classState.message} onRetry={classState.onRetry} />
        ) : revenueChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={revenueChartData}
                cx="50%"
                cy="50%"
                innerRadius={70}
                outerRadius={110}
                paddingAngle={4}
                dataKey="value"
                nameKey="name"
                label={({ name, percentage }) => `${name} ${percentage}%`}
              >
                {revenueChartData.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={FLIGHT_CHART_COLORS[index % FLIGHT_CHART_COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "12px",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                }}
                formatter={(value) => `₦${value.toLocaleString()}`}
              />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-gray-500">No revenue by class available</div>
        )}
      </Card>

      <Card className="p-8 bg-white rounded-2xl shadow-lg shadow-gray-100/50 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
        <div className="mb-6">
          <span className="text-[#1A1A1A] text-base md:text-lg mb-1">
            Revenue Streams
          </span>
          <p className="text-sm text-[#6B7280]">Breakdown by revenue source</p>
        </div>

        {streamsState.loading ? (
          <ListShimmer rows={3} />
        ) : streamsState.error ? (
          <ErrorRetry
            message={streamsState.message}
            onRetry={streamsState.onRetry}
          />
        ) : revenueStreams.length > 0 ? (
          <>
            <div className="space-y-5">
              {revenueStreams.map((stream, index) => {
                const bgColors = [
                  "bg-blue-600",
                  "bg-blue-500",
                  "bg-blue-400",
                  "bg-blue-300",
                ];

                const colorClass = bgColors[index % bgColors.length];

                return (
                  <div key={stream.id || index} className="group">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg ${colorClass} flex items-center justify-center shadow-sm group-hover:shadow-md transition-all`}
                        >
                          <FaDollarSign className="w-5 h-5 text-white" />
                        </div>

                        <div>
                          <div className="text-[#1A1A1A] font-medium">
                            {stream.label}
                          </div>
                          <div className="text-xs text-[#6B7280]">
                            {stream.description}
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-[#1A1A1A] font-semibold text-base md:text-lg">
                          ₦{stream.amount.toLocaleString()}
                        </div>
                        <div className="text-xs text-[#6B7280]">
                          {stream.percentage}%
                        </div>
                      </div>
                    </div>

                    <div className="relative h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`absolute top-0 left-0 h-full ${colorClass} rounded-full transition-all duration-700 ease-out`}
                        style={{ width: `${stream.percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[#6B7280]">
                  Total Revenue Streams
                </span>
                <span className="text-base md:text-lg font-semibold text-[#1A1A1A]">
                  ₦
                  {revenueStreams
                    .reduce((sum, stream) => sum + stream.amount, 0)
                    .toLocaleString()}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-gray-500">No revenue streams available</div>
        )}
      </Card>
    </div>
  );
}
